import { useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Coerce empty strings → null, otherwise Number(value).
function numOrNull(v) {
  if (v === '' || v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}
function intOrNull(v) {
  const n = numOrNull(v)
  return n === null ? null : Math.round(n)
}

// Derive duration in minutes from a HH:MM start_time relative to now.
// Used only as a fallback when the user hasn't typed duration manually.
function deriveDurationMin(startTime) {
  if (!startTime || typeof startTime !== 'string' || !startTime.includes(':')) return null
  const [hh, mm] = startTime.split(':').map(Number)
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null
  const now = new Date()
  const start = new Date()
  start.setHours(hh, mm, 0, 0)
  const diff = Math.round((now.getTime() - start.getTime()) / 60000)
  return diff > 0 && diff < 1440 ? diff : null
}

// Sequential multi-table write. Supabase JS has no client-side
// transactions, so on any child-insert failure we delete the parent
// session and rely on FK cascades to clean exercise_logs/sets/cardio_logs.
export function useSession() {
  const { user } = useAuth()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const saveSession = async (draft) => {
    if (!user) return { ok: false, error: 'Not signed in' }
    setSaving(true)
    setError(null)

    let duration = intOrNull(draft.meta?.duration_min)
    if (!duration) duration = deriveDurationMin(draft.meta?.start_time)

    const { data: session, error: sessionErr } = await supabase
      .from('sessions')
      .insert({
        user_id: user.id,
        date: draft.date,
        session_type: draft.sessionType,
        start_time: draft.meta?.start_time || null,
        duration_min: duration,
        energy_rating: intOrNull(draft.meta?.energy_rating),
        body_weight_kg: numOrNull(draft.meta?.body_weight_kg),
        notes: draft.meta?.notes?.trim() || null,
      })
      .select()
      .single()

    if (sessionErr || !session) {
      setSaving(false)
      const msg = sessionErr?.message || 'Insert failed'
      setError(msg)
      return { ok: false, error: msg }
    }

    const rollback = async () => {
      await supabase.from('sessions').delete().eq('id', session.id)
    }

    for (const log of (draft.logs || [])) {
      const { data: el, error: elErr } = await supabase
        .from('exercise_logs')
        .insert({
          session_id: session.id,
          exercise_id: log.exerciseId,
          order_index: log.order_index ?? 0,
          notes: log.notes?.trim() || null,
        })
        .select()
        .single()
      if (elErr || !el) {
        await rollback()
        setSaving(false)
        const msg = elErr?.message || 'Failed to write exercise log'
        setError(msg)
        return { ok: false, error: msg }
      }

      // Skip rows with reps<=0 — DB CHECK reps>0 would reject them.
      const sets = (log.sets || [])
        .map((s, idx) => ({
          exercise_log_id: el.id,
          set_number: idx + 1,
          weight_kg: numOrNull(s.weight_kg) ?? 0,
          reps: intOrNull(s.reps) ?? 0,
          rpe: intOrNull(s.rpe),
          is_warmup: !!s.is_warmup,
          is_failure: !!s.is_failure,
        }))
        .filter((s) => s.reps > 0)

      if (sets.length) {
        const { error: setErr } = await supabase.from('sets').insert(sets)
        if (setErr) {
          await rollback()
          setSaving(false)
          setError(setErr.message)
          return { ok: false, error: setErr.message }
        }
      }
    }

    if ((draft.cardios || []).length) {
      const cardios = draft.cardios
        .map((c, idx) => ({
          session_id: session.id,
          cardio_type: c.cardio_type,
          order_index: idx,
          duration_min: intOrNull(c.duration_min) ?? 0,
          level: numOrNull(c.level),
          incline: numOrNull(c.incline),
          speed: numOrNull(c.speed),
          distance_km: numOrNull(c.distance_km),
          calories_estimated: intOrNull(c.calories_estimated),
          notes: c.notes?.trim() || null,
        }))
        .filter((c) => c.duration_min > 0)

      if (cardios.length) {
        const { error: cErr } = await supabase.from('cardio_logs').insert(cardios)
        if (cErr) {
          await rollback()
          setSaving(false)
          setError(cErr.message)
          return { ok: false, error: cErr.message }
        }
      }
    }

    setSaving(false)
    return { ok: true, sessionId: session.id }
  }

  // Edit a saved session. Strategy: UPDATE the session row in place, then
  // delete-and-recreate all child rows (exercise_logs cascade to sets,
  // cardio_logs cascade nothing). Simpler than diffing, and acceptable
  // because the user has explicit save/cancel control on the edit page.
  const updateSession = async (sessionId, draft) => {
    if (!user) return { ok: false, error: 'Not signed in' }
    if (!sessionId) return { ok: false, error: 'Missing session id' }
    setSaving(true)
    setError(null)

    let duration = intOrNull(draft.meta?.duration_min)
    if (!duration) duration = deriveDurationMin(draft.meta?.start_time)

    const { error: sessionErr } = await supabase
      .from('sessions')
      .update({
        date: draft.date,
        session_type: draft.sessionType,
        start_time: draft.meta?.start_time || null,
        duration_min: duration,
        energy_rating: intOrNull(draft.meta?.energy_rating),
        body_weight_kg: numOrNull(draft.meta?.body_weight_kg),
        notes: draft.meta?.notes?.trim() || null,
      })
      .eq('id', sessionId)
      .eq('user_id', user.id)
    if (sessionErr) {
      setSaving(false)
      setError(sessionErr.message)
      return { ok: false, error: sessionErr.message }
    }

    // Wipe child rows so we can reinsert from the draft. exercise_logs
    // has ON DELETE CASCADE to sets, so deleting the logs cleans the
    // sets too. cardio_logs is a flat table.
    await supabase.from('exercise_logs').delete().eq('session_id', sessionId)
    await supabase.from('cardio_logs').delete().eq('session_id', sessionId)

    for (const log of (draft.logs || [])) {
      const { data: el, error: elErr } = await supabase
        .from('exercise_logs')
        .insert({
          session_id: sessionId,
          exercise_id: log.exerciseId,
          order_index: log.order_index ?? 0,
          notes: log.notes?.trim() || null,
        })
        .select()
        .single()
      if (elErr || !el) {
        setSaving(false)
        setError(elErr?.message || 'Failed to write exercise log')
        return { ok: false, error: elErr?.message || 'Failed to write exercise log' }
      }

      const sets = (log.sets || [])
        .map((s, idx) => ({
          exercise_log_id: el.id,
          set_number: idx + 1,
          weight_kg: numOrNull(s.weight_kg) ?? 0,
          reps: intOrNull(s.reps) ?? 0,
          rpe: intOrNull(s.rpe),
          is_warmup: !!s.is_warmup,
          is_failure: !!s.is_failure,
        }))
        .filter((s) => s.reps > 0)

      if (sets.length) {
        const { error: setErr } = await supabase.from('sets').insert(sets)
        if (setErr) {
          setSaving(false)
          setError(setErr.message)
          return { ok: false, error: setErr.message }
        }
      }
    }

    if ((draft.cardios || []).length) {
      const cardios = draft.cardios
        .map((c, idx) => ({
          session_id: sessionId,
          cardio_type: c.cardio_type,
          order_index: idx,
          duration_min: intOrNull(c.duration_min) ?? 0,
          level: numOrNull(c.level),
          incline: numOrNull(c.incline),
          speed: numOrNull(c.speed),
          distance_km: numOrNull(c.distance_km),
          calories_estimated: intOrNull(c.calories_estimated),
          notes: c.notes?.trim() || null,
        }))
        .filter((c) => c.duration_min > 0)

      if (cardios.length) {
        const { error: cErr } = await supabase.from('cardio_logs').insert(cardios)
        if (cErr) {
          setSaving(false)
          setError(cErr.message)
          return { ok: false, error: cErr.message }
        }
      }
    }

    setSaving(false)
    return { ok: true, sessionId }
  }

  return { saveSession, updateSession, saving, error }
}
